// services/strategyDraftService.js
//
// The research → bot handoff (PLAN-RESEARCH-UX.md phase C1–C3), browser side.
//
//   POST   /api/strategy-drafts              { source: { kind, ref?, snapshot? }, symbol?, assetClass?, timeframe?, overrides? }
//                                            …or the AUTHORED body (P1.2), below
//     -> 201 { draft }
//     -> 422 NOT_CONVERTIBLE { error, kind, nearest? } — the honest refusal
//     -> 413 SNAPSHOT_TOO_LARGE, 400 UNKNOWN_VARIABLE | INVALID_COMPOSITE | BAD_KIND
//     -> 503 STRATEGY_DRAFTS_UNAVAILABLE (the table is not migrated yet)
//   GET    /api/strategy-drafts[?status=]     -> { drafts }
//   GET    /api/strategy-drafts/:id           -> { draft }
//   PATCH  /api/strategy-drafts/:id           { buy_logic?, sell_logic?, short_*_logic?, notes?, risk?, status?, bot_id? } -> { draft }
//   DELETE /api/strategy-drafts/:id           -> { deleted }
//   POST   /api/strategy-drafts/:id/backtest  { from?, to?, initialCapital?, commission?, slippage? } -> 202 { jobId, draftId }
//   POST   /api/strategy-drafts/:id/promote   -> { draftId, payload, liveReady, liveReason, translation, reportBotIdTo }
//                                            (target 'scanner': + { target, requires, note, warning? }; payload is
//                                            the scanner wizard's POST /api/trading_bots body, status 'scanner_profile')
//   POST   /api/strategy-drafts  { …, target: 'scanner', universe | symbols, topN?, direction?, hold?, trigger?,
//                                  parts?, signal?, studyId?, binding?, sort?, where?,
//                                  legs?: 'long' | 'both', shortTopN? }   (P4.4 — see scannerParams; legs: PLAN-TWO-LEGGED A4)
//     -> 400 TWO_LEGS_SCANNER_ONLY (legs 'both' on a bot draft), 422 NOT_TWO_SIDED (no second leg to hold)
//   GET    /api/bots/signals-capability?symbol=&names=  -> { symbol, names, live, asOf, ageHours, reason, missing, message }
//
// ─── Three contract notes ───────────────────────────────────────────────────
//
// 1. **The server translates, the browser never does.** A page posts the
//    artefact it is showing (`source.snapshot`) and gets back the legs the api
//    decided on. Nothing here builds a rule, and nothing here decides what is
//    convertible — a 422 carries the reason in the user's own words, and the
//    page renders it rather than translating it into "something went wrong".
//
// 2. **Render the DRAFT ROW's legs (`draft.buy_logic` …), never
//    `draft.translation.buy_logic`.** The translation is the record of what the
//    server decided at creation time; the row is what a later PATCH edited. The
//    two diverge the moment a user changes a leg.
//
// 3. **`draftState(err)` splits "this server cannot" from "try again".** A bare
//    404 means the route is not deployed on this api yet (production runs a
//    build without it until the next release) and a 503
//    `STRATEGY_DRAFTS_UNAVAILABLE` means the migration has not been applied —
//    both are capabilities the server lacks, not failures the reader caused, and
//    both end in a direction rather than a retry. An api 404 that carries
//    `code: 'NOT_FOUND'` is a different thing entirely: that draft is gone or
//    was never theirs. Same status, opposite sentence — branch on the code.
//
// ── 4. The AUTHORED body (PLAN-RESEARCH-DESK P1.2) ──────────────────────────
//
// A draft is not only a research artefact: the bot wizard's own Trading Logic
// step creates one when the user presses "Test this rule" (`source.kind:
// 'manual'`), and a TradingView alert bot is one too (`'tradingview'`). Nothing
// is translated for those — the user wrote the rule — so the body states the
// strategy itself:
//
//   { source: { kind: 'manual' }, target: 'bot',
//     symbol, assetClass, timeframe,                     // the api's own vocabulary:
//                                                        // timeframe LOWERCASE and in its
//                                                        // TIMEFRAMES list (no 'tick');
//                                                        // assetClass one of stock_equity |
//                                                        // crypto_spot | crypto_futures
//     buy_logic, sell_logic, short_open_logic, short_close_logic,
//     composites, risk, notes,
//     trade_info }                                       // the REMAINDER of the wizard's block
//
// **The strategy keys are stated top-level ONLY.** The api lifts `symbol`,
// `timeframe`, `asset_class`, `risk`, `composites`, `indicators` and the four
// legs out of `trade_info` onto the row, and a value that appears in both and
// disagrees is a 400 `TRADE_INFO_CONFLICT` — which the wizard would trip on
// every press, because its `trade_info.timeframe` is the select's own casing
// ('1D') and the column's is '1d'. `containers/Trader_Form/botBody.js`
// `draftBodyFromWizard` is the one place that split is made.
//
// **A draft body never carries a secret.** `trade_info.webhook` is dropped
// before the request leaves the browser; the api strips secret-shaped keys at
// any depth as well. Two independent guards, because a stored credential is
// not a bug you get to fix later.
//
// ── 5. The `tradingview` body (PLAN-RESEARCH-DESK P2.2) ─────────────────────
//
// An alert bot is a draft too, and it is the one authored kind with NO rule:
//
//   { source: { kind: 'tradingview' }, target: 'bot',
//     symbol?,                                        // OPTIONAL — an alert can
//                                                     // name its own, and an
//                                                     // EMPTY one must be
//                                                     // omitted, not sent as ''
//     assetClass, risk,
//     trade_info: { …, signalSource: 'tradingview',
//                   webhook: { actionField, buyValue, sellValue } } }
//
// Three differences from the `manual` body, each of them an api rule:
//
//   - **No legs, and no `composites`.** The api REFUSES a leg on this kind
//     (400, `alertHasNoLogicBody`) rather than dropping it: the engine
//     evaluates none, so storing one would be a rule nothing will ever run.
//     The wizard's own composition gives a webhook bot the literal
//     `WEBHOOK_SIGNAL` for both sides, which is why the body is built by
//     `containers/Trader_Form/botBody.js tradingViewDraftBody` and not by
//     `draftBodyFromWizard`.
//   - **No `timeframe`.** An alert has no bar clock; the api stores `''`,
//     which is not in its `TIMEFRAMES` list, so stating one is a 400.
//   - **`webhook` comes BACK, reduced to three fields.** The api keeps
//     `{ actionField, buyValue, sellValue }` and drops anything else; the
//     browser side rebuilds exactly those three from scratch, so the secret is
//     absent by construction rather than by omission.
//
// The draft is written by *Save as draft* — there is no *Test* beside it,
// because an alert-driven bot cannot be backtested here (there is no alert
// history to replay), and `POST /:id/backtest` answers 400 `NOT_BACKTESTABLE`
// with that sentence for exactly this kind.
import http from './http';

const BASE = '/api/strategy-drafts';

/**
 * Every `source.kind` the api's `services/strategyTranslate.js` accepts.
 * The last two are AUTHORED kinds (P1.2/P2.2): the user wrote the rule, so
 * nothing is translated and the body states the legs itself.
 */
export const KINDS = [
  'event_study', 'screen', 'people', 'edge', 'factor', 'blend', 'model', 'ideas',
  'manual', 'tradingview',
];

/** The kinds whose legs the CALLER states, rather than the translation. */
export const AUTHORED_KINDS = ['manual', 'tradingview'];

/**
 * The kinds that are refused by definition, whatever their snapshot says: a
 * ranking is a statement about the cross-section and a single-symbol bot has
 * nowhere to put one. These are pre-disabled in the UI so no round trip is
 * spent learning what the table already says.
 *
 * Ranked *edge* studies refuse too, but WHICH ones is the api's list
 * (`RANKED_STUDIES`) and mirroring it here would drift silently — those are
 * asked, and their 422 is rendered in place of the button.
 */
export const CROSS_SECTIONAL_KINDS = ['factor', 'blend', 'model'];

/** The sentence those three carry, matching the api's own refusal. */
export const CROSS_SECTIONAL_REASON = 'Not convertible to a single-symbol rule: this idea ranks the whole market, and a bot trades one name.';

/**
 * The labelled nearest thing for everything cross-sectional, mirroring the
 * api's `services/strategyTranslate.js CROSS_SECTIONAL_NEAREST`. It is a
 * CONSTANT, not a table that grows, which is why it is safe to state here —
 * the three pre-disabled kinds never reach the api, so nothing else would.
 */
export const CROSS_SECTIONAL_NEAREST = {
  label: 'the nearest live rule — a different idea',
  buy_logic: 'PeerRank > 0.8 AND RelStrength > 0',
  explain:
    'PeerRank and RelStrength are the engine\'s own cross-sectional variables: '
    + '"this name is in the top fifth of its peer group and is outperforming the benchmark". '
    + 'That is a live approximation of being highly ranked — it is not this study\'s rank, '
    + 'it uses a different universe, and none of the study\'s evidence carries over to it.',
};

/** The draft statuses the api stores. */
export const STATUSES = ['draft', 'tested', 'promoted', 'discarded'];

/** The states where asking again cannot help — the server does not have the feature. */
export const DRAFT_UNAVAILABLE = ['missing', 'unavailable'];

/** The four strategy legs, in the order the api's own list has them. */
export const LEG_FIELDS = ['buy_logic', 'sell_logic', 'short_open_logic', 'short_close_logic'];

const draftOf = (res) => res?.data?.draft || null;

/* ── P4.4: the scanner target ─────────────────────────────────────────────── */

/**
 * The kinds the api's `translateToScanner` refuses BY DEFINITION: the two about
 * one name (`SCANNER_SINGLE_SYMBOL_KINDS` — an Event Lab result, a person's
 * filing) and the two authored kinds (a hand-written profile is written in the
 * scanner wizard; an alert is one webhook for one bot). Everything else is
 * ASKED, and a 422 is rendered where the button was — which ranked studies
 * convert is the api's `RANKED_SCANNER_SCORES`, and a copy of it would drift.
 */
export const SCANNER_REFUSED_KINDS = ['event_study', 'people', 'manual', 'tradingview'];

/** Can this kind be offered "Create scanner" at all? */
export const scannerKind = (kind) => !!kind && !SCANNER_REFUSED_KINDS.includes(String(kind));

/**
 * The scanner-target keys `controllers/strategyDrafts.js create` reads off the
 * body (`universe`, `symbols`, `topN`, `direction`, `hold`, `trigger`, `parts`,
 * `signal`, `studyId`, `binding`, `sort`, `where`, and PLAN-TWO-LEGGED's
 * `legs` — 'long' | 'both', a ranked crypto study only — and `shortTopN`). Anything else is dropped,
 * and an empty value is omitted rather than sent — an empty `symbols: []`
 * would reach the api's "no usable tickers" refusal instead of its own
 * universe fallbacks.
 */
export const SCANNER_PARAM_KEYS = [
  'universe', 'symbols', 'topN', 'direction', 'hold', 'trigger', 'parts', 'signal', 'studyId', 'binding', 'sort', 'where',
  'legs', 'shortTopN',
];

export const scannerParams = (scanner = {}) => {
  const out = {};
  for (const k of SCANNER_PARAM_KEYS) {
    const v = scanner[k];
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && !v.length) continue;
    out[k] = v;
  }
  return out;
};

/** Is this draft a scanner profile rather than a bot? (`target` is the api's column.) */
export const isScannerDraft = (draft) => String((draft && draft.target) || 'bot') === 'scanner';

/** The scanner wizard's arrival URL for a scanner draft (a hash, like every research hop). */
export const scannerWizardHref = (id) => `#/profile/scanners?draft=${encodeURIComponent(String(id || ''))}`;

/**
 * Create a draft from a research artefact — or, for an AUTHORED kind, from a
 * rule the user wrote.
 *
 * `source` is `{ kind, ref?, snapshot? }`; the snapshot is the small object the
 * page already has on screen — the headline and the report, never a series (the
 * api strips series keys and refuses over 64 KB).
 *
 * The authored fields are ADD-ONLY: a research caller that passes none of them
 * sends exactly the body it always did. `legs` is `{ buy_logic, sell_logic,
 * short_open_logic, short_close_logic }` and is spread top-level, because the
 * api reads the legs as columns and refuses a leg smuggled inside `trade_info`.
 * An empty leg is dropped rather than sent as `''`.
 */
export const createDraft = ({
  source, symbol, assetClass, timeframe, overrides,
  legs, composites, risk, notes, target, trade_info: tradeInfo,
  scanner,
} = {}) => {
  const body = { source };
  if (symbol) body.symbol = String(symbol).trim().toUpperCase();
  if (assetClass) body.assetClass = assetClass;
  if (timeframe) body.timeframe = timeframe;
  if (overrides && Object.keys(overrides).length) body.overrides = overrides;
  if (target) body.target = target;
  // P4.4 — the scanner target's own inputs, TOP-LEVEL (the api's create path
  // hands them to `translateToScanner` from `req.body`, not from `overrides`).
  if (scanner && typeof scanner === 'object') Object.assign(body, scannerParams(scanner));
  if (legs) {
    for (const key of LEG_FIELDS) {
      const v = legs[key];
      if (typeof v === 'string' && v.trim()) body[key] = v;
    }
  }
  if (Array.isArray(composites) && composites.length) body.composites = composites;
  if (risk && Object.keys(risk).length) body.risk = risk;
  if (notes) body.notes = notes;
  if (tradeInfo && Object.keys(tradeInfo).length) body.trade_info = tradeInfo;
  return http.post(BASE, body).then(draftOf);
};

/** The caller's own drafts, newest first. */
export const listDrafts = (status) =>
  http.get(status ? `${BASE}?status=${encodeURIComponent(status)}` : BASE)
    .then((res) => (Array.isArray(res?.data?.drafts) ? res.data.drafts : []));

export const getDraft = (id) =>
  http.get(`${BASE}/${encodeURIComponent(id)}`).then(draftOf);

/** Edit the legs, the notes, the risk block, the status, or report the bot id. */
export const patchDraft = (id, body) =>
  http.patch(`${BASE}/${encodeURIComponent(id)}`, body || {}).then(draftOf);

/**
 * PLAN-TWO-LEGGED: switch a ranked crypto scanner draft's legs ('both' | 'long').
 * The api re-translates the stored source (futures + `ranking.short`, or the
 * spot profile) and clears the draft's last book test, which described the other one.
 * An EDITED draft is refused (409 DRAFT_EDITED) unless `force` — the
 * re-translation replaces the edits.
 */
export const switchDraftLegs = (id, legs, { force = false } = {}) => patchDraft(id, force ? { legs, force: true } : { legs });

/** What the reader is asked before an edited draft is switched. */
export const SWITCH_LEGS_CONFIRM = 'This draft was edited after it was made from the research. Switching to both legs replaces those edits '
  + 'with the research translation. Switch anyway?';

/**
 * "Switch to both legs" as one call: an edited draft asks first (and sends
 * `force`); a 409 DRAFT_EDITED from a stale page asks then. Resolves to the
 * switched draft, or null when the reader declined.
 */
export const switchToBothLegs = async (draft, confirm = (m) => window.confirm(m)) => {
  const edited = Boolean(draft && draft.translation && draft.translation.edited);
  if (edited && !confirm(SWITCH_LEGS_CONFIRM)) return null;
  try {
    return await switchDraftLegs(draft.id, 'both', { force: edited });
  } catch (err) {
    const code = err && err.response && err.response.data && err.response.data.code;
    if (!edited && code === 'DRAFT_EDITED') {
      if (!confirm(SWITCH_LEGS_CONFIRM)) return null;
      return switchDraftLegs(draft.id, 'both', { force: true });
    }
    throw err;
  }
};

export const deleteDraft = (id) =>
  http.delete(`${BASE}/${encodeURIComponent(id)}`).then((res) => res?.data || null);

/**
 * Run the draft BEFORE it is a bot (C3): 202 `{ jobId, draftId }`. The result
 * is persisted into the caller's own backtest history by the worker's
 * completion hook, which then writes `backtest_id` + `status: 'tested'` back
 * onto the draft — so the draft, re-read, is the source of truth for what ran.
 */
export const runBacktest = (id, body = {}) =>
  http.post(`${BASE}/${encodeURIComponent(id)}/backtest`, body).then((res) => res?.data || null);

/**
 * The wizard prefill (C1). Creates nothing: the user still picks the brokerage
 * account and the size, and the bot is created PAUSED. `liveReady` is
 * re-checked at this moment rather than read off the stored translation.
 */
export const promoteDraft = (id) =>
  http.post(`${BASE}/${encodeURIComponent(id)}/promote`, {}).then((res) => res?.data || null);

/**
 * "Can the live engine evaluate these names for this symbol?" (C0). The same
 * function the activation gate uses, asked before the user presses Start
 * instead of after. Never a 5xx for a missing feed — `live: false` with a
 * `reason` IS the answer.
 */
export const signalsCapability = (symbol, names = []) => {
  const list = (Array.isArray(names) ? names : [names]).filter(Boolean);
  const qs = new URLSearchParams({ symbol: String(symbol || '').trim().toUpperCase() });
  if (list.length) qs.set('names', list.join(','));
  return http.get(`/api/bots/signals-capability?${qs.toString()}`).then((res) => res?.data || null);
};

/**
 * One sentence for a failed draft call, and whether asking again could help.
 *
 * `kind`:
 *   missing        the route is not on this api build yet
 *   unavailable    the table is not migrated on this server yet
 *   notfound       that draft is gone, or was never the caller's
 *   not_convertible the artefact cannot honestly become a rule (`nearest` may name the closest live one)
 *   too_big        the snapshot carried a series
 *   busy           the queue is full, or the caller already has runs in flight
 *   bad            the api refused the request (an unknown variable, a bad kind)
 *   auth / error   the usual two
 */
export const draftState = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  const code = data.code || err?.code;
  const message = data.error || err?.message || 'The request failed.';

  // 503, but not an outage: the handoff table has never been created here.
  if (code === 'STRATEGY_DRAFTS_UNAVAILABLE') {
    return {
      kind: 'unavailable',
      message: "Drafts aren't set up on this server yet",
      direction: 'The handoff table has not been created on this database. See the Data page, or ask whoever runs this server to apply the strategy-drafts migration.',
    };
  }
  // The api's own not-found says so in its code; a bare 404 is an api build
  // that predates the route, which is a capability, not a mistake.
  if (status === 404 && code === 'NOT_FOUND') {
    return { kind: 'notfound', message: message || 'That draft is gone — it was deleted, or it was never yours.', direction: 'Open another idea and save it again.' };
  }
  if (status === 404) {
    return {
      kind: 'missing',
      message: "Saving an idea isn't available on this server yet",
      direction: 'This api build predates the research handoff. It arrives with the next release.',
    };
  }
  // PLAN-TWO-LEGGED A4: both legs asked of a source that has no second leg (422),
  // or of a bot draft (400) — the api's sentence, and what to do instead.
  if (code === 'NOT_TWO_SIDED') {
    // `code` kept: the refusal is of the CHOICE (both legs), not of the source — the long leg still converts.
    return { kind: 'not_convertible', code: 'NOT_TWO_SIDED', message, nearest: null, sourceKind: data.kind || null, direction: 'Create it with the long leg only (spot), or pick a ranked crypto study.' };
  }
  if (code === 'TWO_LEGS_SCANNER_ONLY') {
    return { kind: 'bad', message, direction: 'Both legs make a scanner profile; create the scanner instead.' };
  }
  if (status === 422 || code === 'NOT_CONVERTIBLE') {
    return { kind: 'not_convertible', message, nearest: data.nearest || null, sourceKind: data.kind || null };
  }
  // The two refusals the AUTHORED body can earn (P1.2). Both are bugs in the
  // page rather than mistakes the reader made, and both are `bad` — asking
  // again with the same body cannot help. `TRADE_INFO_TOO_LARGE` is checked
  // HERE rather than under the 413 below, which would call it a snapshot.
  if (code === 'TRADE_INFO_CONFLICT') {
    return {
      kind: 'bad',
      message,
      field: data.field || null,
      direction: 'The rule states this in two places at once — that is a bug in the form, not in your rule.',
    };
  }
  if (code === 'TRADE_INFO_TOO_LARGE') {
    return {
      kind: 'bad',
      message,
      direction: 'The bot settings this rule carries are too big to store — that is a bug in the form, not in your rule.',
    };
  }
  if (status === 413 || code === 'SNAPSHOT_TOO_LARGE') {
    return { kind: 'too_big', message, direction: 'This is a bug in the page, not in your idea — the result it sent was too big.' };
  }
  if (code === 'QUEUE_FULL') {
    return { kind: 'busy', message, direction: 'The backtest queue is full. Try again in a few minutes.' };
  }
  if (status === 429 || code === 'USER_LIMIT') {
    return { kind: 'busy', message, direction: 'Wait for one of your running backtests to finish, or cancel it, then test this again.' };
  }
  if (status === 503) {
    return { kind: 'busy', message, direction: 'The server is busy right now; try again in a minute.' };
  }
  if (status === 400) {
    // UNKNOWN_VARIABLE / INVALID_COMPOSITE / BAD_KIND — the api's message names
    // the offending leg or name, and it is the useful thing to show.
    return { kind: 'bad', message, names: Array.isArray(data.names) ? data.names : null };
  }
  if (status === 401 || status === 403) {
    return { kind: 'auth', message: 'Your session has expired; sign in again.' };
  }
  return { kind: 'error', message, direction: 'Try again in a minute.' };
};

const strategyDraftService = {
  KINDS,
  AUTHORED_KINDS,
  LEG_FIELDS,
  CROSS_SECTIONAL_KINDS,
  CROSS_SECTIONAL_REASON,
  CROSS_SECTIONAL_NEAREST,
  STATUSES,
  DRAFT_UNAVAILABLE,
  SCANNER_REFUSED_KINDS,
  SCANNER_PARAM_KEYS,
  scannerKind,
  scannerParams,
  isScannerDraft,
  scannerWizardHref,
  createDraft,
  listDrafts,
  getDraft,
  patchDraft,
  switchDraftLegs,
  switchToBothLegs,
  deleteDraft,
  runBacktest,
  promoteDraft,
  signalsCapability,
  draftState,
};

export default strategyDraftService;
